import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Home, Search, Ticket, User, Plus, Check, Plane, Users, Users2, Briefcase, CreditCard, ArrowRight, ArrowLeft, MapPin, Car } from "lucide-react";
import PanelShell from "../../components/PanelShell";
import SeatMap from "../../components/SeatMap";
import PassengerPointPicker from "@/components/maps/PassengerPointPicker";
import { useAuth } from "@/context/AuthContext";
import { clientApi } from "@/lib/clientApi";
import { resolveUploadUrl } from "@/lib/api";

// "destino" en aeropuerto_ciudad (el conductor sale del aeropuerto),
// "recojo" en ciudad_aeropuerto -- ver passenger_point_role_for_direction
// en el backend (app/schemas/reservation.py), misma regla replicada acá
// porque el copy hace falta ANTES de crear la reserva (el backend recién
// devuelve passenger_point_role una vez que la reserva ya existe).
const passengerPointCopy = (direction) =>
  direction === "aeropuerto_ciudad"
    ? { title: "¿Dónde te dejamos?", label: "Dirección de destino" }
    : { title: "¿Dónde te recogemos?", label: "Dirección de recojo" };

const navItems = [
  { to: "/cliente", label: "Inicio", icon: Home, end: true },
  { to: "/cliente/buscar", label: "Buscar", icon: Search },
  { to: "/cliente/historial", label: "Reservas", icon: Ticket },
  { to: "/cliente/perfil", label: "Perfil", icon: User },
];
const bottomNav = [
  { to: "/cliente", label: "Inicio", icon: Home, end: true },
  { to: "/cliente/buscar", label: "Buscar", icon: Search },
  { to: "/cliente/reservar", label: "Reservar", icon: Plus },
  { to: "/cliente/historial", label: "Viajes", icon: Ticket },
  { to: "/cliente/perfil", label: "Perfil", icon: User },
];

const CATEGORY_LABELS = { sedan: "Sedán", suv: "SUV", van: "Van" };

const EMPTY_FORM = {
  flight_airline: "",
  flight_number: "",
  flight_origin: "",
  flight_arrival_at: "",
  contact_name: "",
  contact_document: "",
  contact_phone: "",
  contact_email: "",
  adults_count: 1,
  children_count: 0,
  luggage_large: 0,
  luggage_small: 0,
  special_requirements: "",
  passenger_point_address: "",
  passenger_point_reference: "",
  passenger_point_lat: null,
  passenger_point_lng: null,
  payment_method: "yape",
};

const ClientBooking = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const routeId = searchParams.get("routeId");

  const [route, setRoute] = useState(null);
  const [loadingRoute, setLoadingRoute] = useState(true);
  const [modality, setModality] = useState(null);

  const [departures, setDepartures] = useState([]);
  const [selectedDepartureId, setSelectedDepartureId] = useState("");
  const [seatLayout, setSeatLayout] = useState([]);
  const [selectedSeats, setSelectedSeats] = useState([]);

  const [privateDate, setPrivateDate] = useState("");
  const [privateTime, setPrivateTime] = useState("");

  const [form, setForm] = useState(EMPTY_FORM);
  const [stepIndex, setStepIndex] = useState(0);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const selectedDeparture = departures.find((d) => d.id === selectedDepartureId) || null;

  useEffect(() => {
    setForm((f) => ({
      ...f,
      contact_name: user?.full_name || "",
      contact_phone: user?.phone || "",
      contact_email: user?.email || "",
    }));
  }, [user]);

  useEffect(() => {
    if (!routeId) {
      setRoute(null);
      setLoadingRoute(false);
      return;
    }
    clientApi
      .getRoute(routeId)
      .then(({ data }) => {
        setRoute(data);
        setModality(data.shared_enabled ? "compartido" : "privado");
      })
      .catch(() => setRoute(null))
      .finally(() => setLoadingRoute(false));
  }, [routeId]);

  useEffect(() => {
    if (modality === "compartido" && route) {
      clientApi.listDepartures(route.id).then(({ data }) => setDepartures(data));
    }
  }, [modality, route]);

  useEffect(() => {
    if (selectedDeparture) {
      clientApi.getSeatLayout(selectedDeparture.total_seats).then(({ data }) => setSeatLayout(data.seats));
      setSelectedSeats([]);
    } else {
      setSeatLayout([]);
    }
  }, [selectedDepartureId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSeat = (seatNumber) => {
    setSelectedSeats((prev) => (prev.includes(seatNumber) ? prev.filter((s) => s !== seatNumber) : [...prev, seatNumber]));
  };

  const estimatedPrice = useMemo(() => {
    if (!route) return null;
    if (modality === "compartido") return route.price_per_seat ? (Number(route.price_per_seat) * selectedSeats.length).toFixed(2) : null;
    return route.private_price;
  }, [route, modality, selectedSeats]);

  const steps = useMemo(
    () => [
      { key: "modalidad", label: "Modalidad", icon: Users2 },
      { key: "vuelo", label: "Vuelo", icon: Plane },
      { key: "contacto", label: "Contacto", icon: User },
      { key: "pasajeros", label: "Pasajeros", icon: Users },
      { key: "destino", label: "Punto", icon: MapPin },
      { key: "pago", label: "Pago", icon: CreditCard },
    ],
    []
  );

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  // PassengerPointPicker es genérico (address/reference/lat/lng) para
  // poder reusarse fuera de este formulario -- acá se traduce a los
  // nombres que espera el backend (passenger_point_*).
  const updatePassengerPoint = (patch) =>
    setForm((f) => ({
      ...f,
      ...(patch.address !== undefined ? { passenger_point_address: patch.address } : {}),
      ...(patch.reference !== undefined ? { passenger_point_reference: patch.reference } : {}),
      ...(patch.lat !== undefined ? { passenger_point_lat: patch.lat } : {}),
      ...(patch.lng !== undefined ? { passenger_point_lng: patch.lng } : {}),
    }));

  const canAdvanceFromModalidad =
    modality === "compartido" ? Boolean(selectedDepartureId) && selectedSeats.length > 0 : Boolean(privateDate) && Boolean(privateTime);
  const canAdvanceFromDestino = Boolean(form.passenger_point_address);

  const submit = async () => {
    setError("");
    setSubmitting(true);
    try {
      const payload = {
        route_id: route.id,
        modality,
        contact_name: form.contact_name,
        contact_document: form.contact_document || null,
        contact_phone: form.contact_phone,
        contact_email: form.contact_email || null,
        flight_airline: form.flight_airline || null,
        flight_number: form.flight_number || null,
        flight_origin: form.flight_origin || null,
        flight_arrival_at: form.flight_arrival_at ? `${form.flight_arrival_at}:00-05:00` : null,
        adults_count: Number(form.adults_count),
        children_count: Number(form.children_count),
        luggage_large: Number(form.luggage_large),
        luggage_small: Number(form.luggage_small),
        special_requirements: form.special_requirements || null,
        payment_method: form.payment_method,
        // Obligatorio en ambas modalidades -- el conductor lo necesita en
        // compartido para optimizar su ruta de entrega entre pasajeros
        // (ver backend/app/models/reservation.py).
        passenger_point_address: form.passenger_point_address,
        passenger_point_reference: form.passenger_point_reference || null,
        passenger_point_lat: form.passenger_point_lat,
        passenger_point_lng: form.passenger_point_lng,
      };
      if (modality === "compartido") {
        payload.departure_id = selectedDepartureId;
        payload.seat_numbers = selectedSeats;
      } else {
        // Perú es UTC-5 fijo -- ver backend/app/core/timezones.py.
        payload.scheduled_at = `${privateDate}T${privateTime}:00-05:00`;
      }
      const { data } = await clientApi.createReservation(payload);
      navigate(`/cliente/ticket?reservationId=${data.id}`);
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo crear la reserva. Intenta de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingRoute) {
    return (
      <PanelShell role="Cliente" navItems={navItems} bottomNavItems={bottomNav} userName={user?.full_name} testId="client-booking">
        <div className="max-w-5xl mx-auto px-4 lg:px-8 py-12 text-sm text-[#A0A0A5] font-body">Cargando…</div>
      </PanelShell>
    );
  }

  if (!route) {
    return (
      <PanelShell role="Cliente" navItems={navItems} bottomNavItems={bottomNav} userName={user?.full_name} testId="client-booking">
        <div className="max-w-5xl mx-auto px-4 lg:px-8 py-12">
          <p className="text-sm text-[#A0A0A5] font-body">No se encontró la ruta. <Link to="/cliente/buscar" className="text-[#C9A227]">Volver a buscar</Link></p>
        </div>
      </PanelShell>
    );
  }

  const currentKey = steps[stepIndex].key;

  return (
    <PanelShell role="Cliente" navItems={navItems} bottomNavItems={bottomNav} userName={user?.full_name} testId="client-booking">
      <div className="max-w-5xl mx-auto px-4 lg:px-8 py-8 lg:py-12">
        <p className="text-[10px] uppercase tracking-brand text-[#C9A227] font-display">Nueva reserva</p>
        <h1 className="font-display font-bold text-3xl uppercase tracking-tight mt-1">Detalles de tu traslado</h1>

        <div className="mt-8 overflow-x-auto">
          <div className="flex items-center gap-2 min-w-max" data-testid="booking-stepper">
            {steps.map((s, i) => {
              const Icon = s.icon;
              const done = stepIndex > i;
              const active = stepIndex === i;
              return (
                <React.Fragment key={s.key}>
                  <button
                    onClick={() => setStepIndex(i)}
                    className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border transition-all ${active ? "border-[#C9A227] bg-[#C9A227]/10" : done ? "border-[#C9A227]/40 bg-[#1C1D20]" : "border-[#2E2F33] bg-[#1C1D20]"}`}
                    data-testid={`step-${s.key}`}
                  >
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-display font-bold ${active || done ? "bg-[#C9A227] text-[#0D0E10]" : "bg-[#2E2F33] text-[#A0A0A5]"}`}>
                      {done ? <Check size={12} /> : i + 1}
                    </div>
                    <span className={`text-xs uppercase tracking-wider font-display ${active ? "text-[#C9A227]" : done ? "text-[#F5F5F5]" : "text-[#A0A0A5]"}`}>{s.label}</span>
                  </button>
                  {i < steps.length - 1 && <span className={`w-6 h-px ${done ? "bg-[#C9A227]" : "bg-[#2E2F33]"}`} />}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-[#1C1D20] border border-[#2E2F33] rounded-xl p-6 lg:p-8" data-testid="booking-form">
            {error && <p className="mb-4 text-xs text-red-400 font-body">{error}</p>}

            {currentKey === "modalidad" && (
              <div>
                <h3 className="font-display font-bold text-xl uppercase tracking-wide mb-2">Elige cómo viajas</h3>
                <p className="text-sm text-[#A0A0A5] font-body mb-6">
                  {route.airport_name} → {route.city}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                  {route.shared_enabled && (
                    <button
                      onClick={() => setModality("compartido")}
                      className={`text-left p-5 border rounded-xl transition ${modality === "compartido" ? "border-[#C9A227] bg-[#C9A227]/10" : "border-[#2E2F33] bg-[#0D0E10] hover:border-[#C9A227]/40"}`}
                      data-testid="modality-compartido"
                    >
                      <p className="font-display font-bold uppercase tracking-wider text-sm flex items-center gap-2"><Users2 size={14} /> Compartido</p>
                      <p className="text-xs text-[#A0A0A5] mt-1">Desde S/ {route.price_per_seat} por asiento</p>
                    </button>
                  )}
                  {route.private_enabled && (
                    <button
                      onClick={() => setModality("privado")}
                      className={`text-left p-5 border rounded-xl transition ${modality === "privado" ? "border-[#C9A227] bg-[#C9A227]/10" : "border-[#2E2F33] bg-[#0D0E10] hover:border-[#C9A227]/40"}`}
                      data-testid="modality-privado"
                    >
                      <p className="font-display font-bold uppercase tracking-wider text-sm flex items-center gap-2"><Car size={14} /> Privado</p>
                      <p className="text-xs text-[#A0A0A5] mt-1">S/ {route.private_price} · vehículo completo</p>
                    </button>
                  )}
                </div>

                {modality === "compartido" && (
                  <div>
                    <label className="text-[10px] uppercase tracking-brand text-[#A0A0A5] font-display block mb-2">Salida</label>
                    <select
                      value={selectedDepartureId}
                      onChange={(e) => setSelectedDepartureId(e.target.value)}
                      className="w-full bg-[#0D0E10] border border-[#2E2F33] focus:border-[#C9A227] rounded-lg px-4 py-3 text-sm text-[#F5F5F5] font-body outline-none mb-6"
                      data-testid="select-departure"
                    >
                      <option value="">Elige fecha y hora</option>
                      {departures.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.departure_date} · {d.departure_time.slice(0, 5)} ({d.total_seats - d.taken_seats.length} libres)
                        </option>
                      ))}
                    </select>

                    {selectedDeparture && (
                      <SeatMap
                        layout={seatLayout}
                        takenSeats={selectedDeparture.taken_seats}
                        selectedSeats={selectedSeats}
                        onToggleSeat={toggleSeat}
                      />
                    )}
                  </div>
                )}

                {modality === "privado" && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Fecha" type="date" value={privateDate} onChange={(e) => setPrivateDate(e.target.value)} testId="private-date" />
                    <Field label="Hora" type="time" value={privateTime} onChange={(e) => setPrivateTime(e.target.value)} testId="private-time" />
                    <p className="text-xs text-[#A0A0A5] sm:col-span-2">
                      Disponible entre {route.available_from?.slice(0, 5)} y {route.available_until?.slice(0, 5)}.
                    </p>
                  </div>
                )}
              </div>
            )}

            {currentKey === "vuelo" && (
              <div>
                <h3 className="font-display font-bold text-xl uppercase tracking-wide mb-2">Información de vuelo</h3>
                <p className="text-sm text-[#A0A0A5] font-body mb-6">Opcional. Monitoreamos tu vuelo si lo indicas.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Aerolínea" value={form.flight_airline} onChange={update("flight_airline")} testId="flight-airline" />
                  <Field label="N° de vuelo" value={form.flight_number} onChange={update("flight_number")} testId="flight-number" />
                  <Field label="Origen del vuelo" value={form.flight_origin} onChange={update("flight_origin")} testId="flight-from" />
                  <Field label="Hora de llegada" type="datetime-local" value={form.flight_arrival_at} onChange={update("flight_arrival_at")} testId="flight-arrival" />
                </div>
              </div>
            )}

            {currentKey === "contacto" && (
              <div>
                <h3 className="font-display font-bold text-xl uppercase tracking-wide mb-2">Datos de contacto</h3>
                <p className="text-sm text-[#A0A0A5] font-body mb-6">Te enviaremos confirmación y datos del chofer.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Nombre completo" value={form.contact_name} onChange={update("contact_name")} testId="contact-name" required />
                  <Field label="Documento" value={form.contact_document} onChange={update("contact_document")} testId="contact-doc" />
                  <Field label="Teléfono / WhatsApp" value={form.contact_phone} onChange={update("contact_phone")} testId="contact-phone" required />
                  <Field label="Correo" value={form.contact_email} onChange={update("contact_email")} testId="contact-email" />
                </div>
              </div>
            )}

            {currentKey === "pasajeros" && (
              <div>
                <h3 className="font-display font-bold text-xl uppercase tracking-wide mb-2">Pasajeros y equipaje</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field type="number" label="Adultos" value={form.adults_count} onChange={update("adults_count")} testId="pax-adults" />
                  <Field type="number" label="Niños" value={form.children_count} onChange={update("children_count")} testId="pax-children" />
                  <Field type="number" label="Maletas grandes" value={form.luggage_large} onChange={update("luggage_large")} testId="pax-large" />
                  <Field type="number" label="Maletas pequeñas" value={form.luggage_small} onChange={update("luggage_small")} testId="pax-small" />
                </div>
                <div className="mt-6">
                  <label className="text-[10px] uppercase tracking-brand text-[#A0A0A5] font-display block mb-2">Requerimientos especiales</label>
                  <textarea
                    rows={3}
                    value={form.special_requirements}
                    onChange={update("special_requirements")}
                    className="w-full bg-[#0D0E10] border border-[#2E2F33] focus:border-[#C9A227] rounded-lg px-4 py-3 text-sm text-[#F5F5F5] font-body outline-none"
                    placeholder="Silla infantil, agua, temperatura..."
                    data-testid="pax-notes"
                  />
                </div>
              </div>
            )}

            {currentKey === "destino" && (
              <div>
                <h3 className="font-display font-bold text-xl uppercase tracking-wide mb-2">
                  {passengerPointCopy(route.direction).title}
                </h3>
                <p className="text-sm text-[#A0A0A5] font-body mb-6">
                  {modality === "compartido"
                    ? "Tu conductor usa este punto para ordenar las paradas del viaje compartido."
                    : "Ajusta el pin para precisar el punto exacto."}
                </p>
                <PassengerPointPicker
                  label={passengerPointCopy(route.direction).label}
                  address={form.passenger_point_address}
                  reference={form.passenger_point_reference}
                  lat={form.passenger_point_lat}
                  lng={form.passenger_point_lng}
                  onChange={updatePassengerPoint}
                  testId="passenger-point"
                />
              </div>
            )}

            {currentKey === "pago" && (
              <div>
                <h3 className="font-display font-bold text-xl uppercase tracking-wide mb-2">Método de pago</h3>
                <p className="text-sm text-[#A0A0A5] font-body mb-6">Elige cómo prefieres pagar tu traslado.</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[
                    { id: "yape", name: "Yape", desc: "Pago inmediato al confirmar" },
                    { id: "transferencia", name: "Transferencia", desc: "BCP · Interbank" },
                    { id: "efectivo", name: "Efectivo", desc: "Al conductor" },
                  ].map((m) => (
                    <button
                      key={m.id}
                      onClick={() => setForm((f) => ({ ...f, payment_method: m.id }))}
                      className={`text-left p-5 border rounded-xl transition ${form.payment_method === m.id ? "border-[#C9A227] bg-[#C9A227]/10" : "border-[#2E2F33] bg-[#0D0E10] hover:border-[#C9A227]/40"}`}
                      data-testid={`pay-${m.id}`}
                    >
                      <p className="font-display font-bold uppercase tracking-wider text-sm">{m.name}</p>
                      <p className="text-xs text-[#A0A0A5] mt-1">{m.desc}</p>
                    </button>
                  ))}
                </div>
                <div className="mt-6 p-5 bg-[#0D0E10] border border-[#2E2F33] rounded-lg text-xs text-[#A0A0A5] font-body">
                  El pago con Yape/transferencia se confirma manualmente por el conductor o el admin al recibir el comprobante.
                </div>
              </div>
            )}

            <div className="mt-8 flex items-center justify-between border-t border-[#2E2F33] pt-6">
              <button
                onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
                disabled={stepIndex === 0}
                className="inline-flex items-center gap-2 px-5 py-2.5 border border-[#2E2F33] hover:border-[#C9A227] text-[#F5F5F5] uppercase tracking-wider font-display text-xs rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="prev-step-btn"
              >
                <ArrowLeft size={12} /> Atrás
              </button>
              {stepIndex < steps.length - 1 ? (
                <button
                  onClick={() => setStepIndex((i) => i + 1)}
                  disabled={(currentKey === "modalidad" && !canAdvanceFromModalidad) || (currentKey === "destino" && !canAdvanceFromDestino)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#C9A227] hover:bg-[#D4AF37] disabled:opacity-40 disabled:cursor-not-allowed text-[#0D0E10] font-display font-bold uppercase tracking-wider text-xs rounded-lg"
                  data-testid="next-step-btn"
                >
                  Continuar <ArrowRight size={12} />
                </button>
              ) : (
                <button
                  onClick={submit}
                  disabled={submitting || !canAdvanceFromDestino}
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#C9A227] hover:bg-[#D4AF37] disabled:opacity-60 text-[#0D0E10] font-display font-bold uppercase tracking-wider text-xs rounded-lg"
                  data-testid="confirm-booking-btn"
                >
                  {submitting ? "Confirmando…" : "Confirmar reserva"} <Check size={14} />
                </button>
              )}
            </div>
          </div>

          <aside className="lg:sticky lg:top-24 h-fit bg-[#1C1D20] border border-[#C9A227]/30 rounded-xl p-6" data-testid="booking-summary">
            <p className="text-[10px] uppercase tracking-brand text-[#C9A227] font-display mb-4">Resumen</p>
            <div className="space-y-3 text-sm">
              <SummaryRow label="Ruta" value={`${route.airport_name} → ${route.city}`} />
              <SummaryRow label="Modalidad" value={modality === "compartido" ? "Compartido" : "Privado"} />
              {modality === "compartido" && selectedDeparture && (
                <>
                  <SummaryRow label="Salida" value={`${selectedDeparture.departure_date} · ${selectedDeparture.departure_time.slice(0, 5)}`} />
                  <SummaryRow label="Asientos" value={selectedSeats.length > 0 ? selectedSeats.join(", ") : "—"} />
                </>
              )}
              {modality === "privado" && <SummaryRow label="Fecha y hora" value={privateDate && privateTime ? `${privateDate} ${privateTime}` : "—"} />}
              {route.vehicle && (
                <SummaryRow label="Vehículo" value={`${route.vehicle.brand} ${route.vehicle.model} (${CATEGORY_LABELS[route.vehicle.category] || route.vehicle.category})`} />
              )}
            </div>
            <div className="hr-gold my-5" />
            <div className="flex items-baseline justify-between mt-1">
              <span className="text-xs uppercase tracking-wider text-[#A0A0A5]">Total</span>
              <span className="font-display font-bold text-2xl text-[#C9A227]">S/ {estimatedPrice ?? "—"}</span>
            </div>
          </aside>
        </div>
      </div>
    </PanelShell>
  );
};

const Field = ({ label, testId, ...props }) => (
  <div>
    <label className="text-[10px] uppercase tracking-brand text-[#A0A0A5] font-display block mb-2">{label}</label>
    <input {...props} className="w-full bg-[#0D0E10] border border-[#2E2F33] focus:border-[#C9A227] rounded-lg px-4 py-3 text-sm text-[#F5F5F5] font-body outline-none transition-colors" data-testid={testId} />
  </div>
);

const SummaryRow = ({ label, value }) => (
  <div className="flex justify-between gap-3">
    <span className="text-xs uppercase tracking-wider text-[#A0A0A5] font-display shrink-0">{label}</span>
    <span className="text-xs text-[#F5F5F5] font-body text-right">{value}</span>
  </div>
);

export default ClientBooking;
